import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import TodayMapCount from "../common/TodayMapCount";
import TotalMapCount from "../common/TotalMapCount";
import LoginStatus from "./LoginStatus";
import styles from "./Title.module.css";
import logo from "../../img/logo.png";
import { Link, useNavigate } from "react-router-dom";
import { decodeUnicode, MapCount, path } from "../common/Common";

import { useModal } from "../modal/Modal";
import LoginForm from "../login/LoginForm";
import SignUpModal from "../login/SignUpModal";
import { useLoginStatus } from "../../hooks/useLoginStatus";
import { getCookie } from "../../module/auth-lite";

interface TitleProps {
    mapCount: (...args: any) => any;
    // login: (...args: any) => any; // 백엔드 연결 전 prop이므로 주석처리 | 250905 | smyun
    logout: (...args: any) => any;
}
/**
 * @author mj
 * @date 2022-08-24 09:24:34
 * @remark 상단 헤더
 * @param props TitleProps
 * @props mapCount(required) : 오늘의 지도&전체 지도 수 카운트 콜백
 *        login(required) : 로그인 버튼 눌렀을 때 실행 콜백
 *        logout(required) : 로그아웃 버튼 눌렀을때 실행 콜백
 *        mypage(required) : 마이페이지 버튼 클릭했을 때 마이페이지 이동 콜백
 */
function Title(props: TitleProps) {
    const navigate = useNavigate();
    const isLogin = useLoginStatus();
    const [cookies, setCookies, removeCookies] = useCookies(["KMUN"]);
    const [nickname, setNickname] = useState<string>(""); // 표시용 닉네임
    const [mapCount, setMapCount] = useState<MapCount>();

    // 💠 25.03.24.MON | YoungJun | 모달 State 수정
    const { ModalComponent, openModal, closeModal } = useModal();

    // 쿠키
    /**
     * 오늘의 지도/전체 지도 개수 표시,
     * 쿠키 로그인 여부 및 유저명 표시 함수
     */
    useEffect(() => {
        // 오늘의 지도/전체 지도 카운트
        const getMapCount = async () => {
            const result: MapCount = await props.mapCount();
            result && setMapCount(result);
        };

        getMapCount();
    }, [props]);

    // 닉네임 동기화 방법 수정 | smyun | 250905
    useEffect(() => {
        const sync = () => {
            const raw = getCookie("KMUN") ?? "";
            // 디코딩
            const decoded = raw ? decodeUnicode(raw) : "";
            setNickname(decoded);
        };
        sync();
        if (isLogin && !getCookie("KMUN")) {
            // 서버가 KMUN 세팅하기 전에 먼저 isLogin이 true가 될 수 있어 한 번 정도 재시도
            const t = setTimeout(sync, 300);
            return () => clearTimeout(t);
        }
    }, [isLogin, cookies.KMUN]);

    // 닉네임 변경시 탭간 동기화 bc 추가 | smyun | 250908
    useEffect(() => {
        const bc = new BroadcastChannel("auth-channel");
        bc.onmessage = (e) => {
            if (e.data?.type === "nickname-updated") {
                setNickname(e.data.nickname);
            }
        };
        return () => bc.close();
    }, []);

    const logout = () => {
        props.logout();
        navigate(path.ROOT, { replace: true });
    };

    const onClickSignUp = () => {
        openModal({
            title: "회원가입",
            children: <SignUpModal onClose={() => closeModal()} />,
            modalButtons: null,
            size: "normal",
        });
    };

    //

    const [tempPopOpen, setTempPopOpen] = useState(false);

    // useEffect(() => {
    //     setTempPopOpen(true)
    // }, [])

    useEffect(() => {
        if (tempPopOpen) {
            openModal({
                title: "공지사항",
                children: (
                    <div>
                        <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#111' }}>
                            정기 소방 점검에 따른 서비스 일시 중단 안내
                        </h3>
                        <p style={{ margin: '0 0 12px 0', color: '#444' }}>
                            안녕하세요, 서비스 이용에 깊이 감사드립니다.<br />
                            서버실이 위치한 건물 내 정기 소방 시설 점검으로 인해<br />
                            안전상의 이유로 서비스가 일시 중단될 예정입니다.
                        </p>
                        <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '8px', margin: '16px 0', textAlign: 'left', fontSize: '14px' }}>
                            • <strong>중단 일시:</strong> [2026년 9월 13일(일) 09:00 ~ 9월 13일(일) 16:00]<br />
                            • <strong>중단 사유:</strong> 건물 소방 설비 점검에 따른 서버실 단전 및 점검<br />
                            • <strong>정상화 예정:</strong> [익일 9월 14일(월) 09:00] 순차적 재가동
                        </div>
                        <p style={{ margin: '0', fontSize: '13px', color: '#666' }}>
                            점검 완료 후 신속히 서버를 순차 재가동하여 서비스를 정상화하겠습니다.<br />
                            이용에 불편을 드려 죄송하며, 고객님의 너른 양해 부탁드립니다.
                        </p>
                    </div>
                ),
                modalButtons: null,
                size: "wide",
            })
        }
    }, [tempPopOpen, openModal])

    return (
        <div className={styles.header}>
            <div className={styles.wrapper}>
                <div className={styles.count_bx}>
                    <TodayMapCount size="small" today={mapCount ? mapCount.today : 0} />
                    <TotalMapCount size="small" total={mapCount ? mapCount.total : 0} isAnimated={true} />
                </div>
                <Link to={path.ROOT} className={styles.logo}>
                    <img src={logo} alt="Logo" />
                </Link>
                <div className={styles.member_bx}>
                    {isLogin ? (
                        <LoginStatus member={nickname} logout={logout} />
                    ) : (
                        <button
                            className={styles.login}
                            onClick={() =>
                                openModal({
                                    title: "로그인",
                                    children: <LoginForm onClose={() => closeModal()} onClickSignUp={onClickSignUp} />,
                                    modalButtons: null,
                                    size: "wide",
                                    disableOverlayClose: true,
                                })
                            }
                        >
                            {" "}
                            로그인
                        </button>
                    )}
                </div>
            </div>

            <ModalComponent />
        </div>
    );
}

export default Title;
